import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import http from '@/services/http';
import {
    downloadPresignedUrlAsBlob,
    fetchBackupPresignedDescriptor,
    fetchPlaybackDescriptor,
    resolvePlaybackUrl,
    type AudioPlaybackDescriptor,
} from '@/composables/api/useAudioFileIssuesApi';
import { fetchRawDdexTrackAudioDescriptor } from '@/composables/api/useTrackAudioApi';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';
import {
    clearPersistedQueue,
    readPersistedQueue,
    writePersistedQueue,
} from '@/stores/playerQueuePersistence';
import {
    canReresolveIdentity,
    isSameQueueOccurrence,
    QUEUE_MAX_ENTRIES,
    supportsDeepCheck,
    type PlaybackSourceSlug,
    type QueueEntry,
    type QueueEntryRecordLinks,
    type QueueIdentity,
    type RepeatMode,
} from '@/types/playerQueue';

export type { PlaybackSourceSlug } from '@/types/playerQueue';

type PlaybackMode = 'idle' | 'mp3_loading' | 'mp3_ready' | 'hls';

/** An audio source ready to be handed to the `<audio>` element. */
type PreparedPlayable =
    | { kind: 'object_url'; url: string }
    | { kind: 'hls'; url: string };

/**
 * How many entries in a row may vanish (404) before we stop assuming it's
 * ordinary catalogue churn. A queue restored after a purge must not fire 500
 * silent requests — see the failure-modes section of the spec.
 */
const MAX_CONSECUTIVE_MISSING_ENTRIES = 10;

/** Previous restarts the current track above this; below it, it steps back. */
const PREVIOUS_RESTARTS_ABOVE_SECONDS = 3;

/**
 * Distinguishes ids minted by THIS page load from ids restored from an earlier
 * one.
 *
 * The counter alone is not enough. It is module state, so it restarts at zero on
 * every load, while a restored queue keeps the ids it was persisted with — which
 * were minted by a counter that also started at zero. So the first entry
 * appended after a reload used to be `qe_1`, an id the restored queue was
 * already holding.
 *
 * That is not cosmetic. `entryId` is the key for removal, for jumping, and for
 * the prefetch slot: two entries sharing one means removing either removes both,
 * and a blob fetched for one can be handed to the other — the exact failure
 * entry-keyed prefetch exists to make impossible.
 *
 * Not `crypto.randomUUID` — jsdom lacks it under some configs.
 */
const entryIdLoadPrefix = Math.random().toString(36).slice(2, 8);
let entryIdCounter = 0;

/** Unique per occurrence, and across page loads. */
function nextEntryId(): string {
    entryIdCounter += 1;

    return `qe_${entryIdLoadPrefix}_${entryIdCounter}`;
}

export function buildQueueEntry(options: {
    identity: QueueIdentity;
    source: PlaybackSourceSlug;
    code: string;
    title?: string | null;
    artist?: string | null;
    album?: string | null;
    coverUrl?: string | null;
    position?: number | null;
    /**
     * The full track row, for entries that came from a list. Omitted everywhere
     * else — see {@link QueueEntry.card}, where null is a normal end state
     * rather than a gap.
     */
    card?: PlaylistTrackRow | null;
} & Partial<QueueEntryRecordLinks>): QueueEntry {
    return {
        entryId: nextEntryId(),
        identity: options.identity,
        source: options.source,
        code: options.code.toUpperCase(),
        title: options.title ?? null,
        artist: options.artist ?? null,
        album: options.album ?? null,
        coverUrl: options.coverUrl ?? null,
        position: options.position ?? null,
        trackEntityId: options.trackEntityId ?? null,
        trackRecordId: options.trackRecordId ?? null,
        artistId: options.artistId ?? null,
        albumId: options.albumId ?? null,
        failureReason: null,
        card: options.card ?? null,
    };
}

/**
 * Display fields the three `loadAndPlay*` entry points share, plus the optional
 * records their lines link to. Kept as one type so a new identity field is added
 * in one place rather than remembered in three.
 */
export type PlaybackDisplayOptions = {
    code: string;
    title: string | null;
    artist?: string | null;
    album?: string | null;
    coverUrl: string | null;
} & Partial<QueueEntryRecordLinks>;

/** Narrow a caller's options down to just the record links, all defaulted. */
function recordLinksFrom(options: Partial<QueueEntryRecordLinks>): QueueEntryRecordLinks {
    return {
        trackEntityId: options.trackEntityId ?? null,
        trackRecordId: options.trackRecordId ?? null,
        artistId: options.artistId ?? null,
        albumId: options.albumId ?? null,
    };
}

/** True when the rejection says the track is gone, rather than broken. */
function isMissingResourceError(error: unknown): boolean {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;

    return status === 404 || status === 410;
}

function errorText(error: unknown, fallback: string): string {
    return error instanceof Error && error.message !== '' ? error.message : fallback;
}

/**
 * Global diagnostics player, and the queue behind it.
 *
 * The queue lives here rather than in a sibling store because there is no
 * second mode to keep in sync: a one-off play is simply a queue of length one,
 * so `loadAndPlay` / `loadAndPlayWithDescriptor` / `loadAndPlayLocalFile` keep
 * their signatures and every existing call site is untouched. It also keeps
 * `@ended` on the element this store already owns, so auto-advance cannot fire
 * twice through a cross-store hop.
 *
 * Two mechanisms carry the concurrency correctness:
 *
 *  - **`loadGeneration`** — bumped by every new load (and by close/suspend), and
 *    re-checked after every `await`. A stale generation means someone took over,
 *    so the losing load sets no state and revokes whatever it built. Queue
 *    *mutations* deliberately do NOT bump it: reordering the queue view while a
 *    track downloads must not abandon that download.
 *  - **Entry-keyed prefetch** — {@link takePrefetchedFor} is the only code that
 *    may move a URL from the prefetch slot to the playing slot, and it does so
 *    only for the entry the blob was fetched for. That inversion is what makes a
 *    missed invalidation a memory leak rather than the wrong track playing.
 */
export const useAudioPlaybackStore = defineStore('audioPlayback', () => {
    const audioEl = shallowRef<HTMLAudioElement | null>(null);

    const visible = ref(false);
    const errorMessage = ref<string | null>(null);

    const trackCodeUpper = ref<string | null>(null);
    const trackTitle = ref<string | null>(null);
    // The display lines next to the cover art mirror the History Inspector /
    // AudioFileIssueCard layout: title (+ remix) on top, then artist, then
    // album. Callers pass these in; they fall back to `null` when not known
    // (e.g. diagnostics playback) and the template renders an "Unknown …"
    // placeholder rather than collapsing the row.
    //
    // These stay writable refs, NOT computeds derived from the current entry:
    // playerStore.test.ts and AudioPlayer.test.ts both assign them directly to
    // stage the bar without media plumbing, and deriving them would break those
    // tests (and with them the spec's "existing tests pass unmodified" gate).
    // `applyEntryIdentity` is what keeps them in step with the queue.
    const trackArtist = ref<string | null>(null);
    const trackAlbum = ref<string | null>(null);
    const coverArtUrl = ref<string | null>(null);
    // Records behind the three identity lines, when the call site knows them.
    // The player bar turns each line it has an id for into a link that opens
    // that record in the inspector; the rest stay plain text. Diagnostics
    // playback (a manually uploaded file, an unresolved player check-in) has
    // no records to point at, so all three stay null and nothing is clickable.
    const trackEntityId = ref<string | null>(null);
    const trackRecordId = ref<number | string | null>(null);
    const trackArtistId = ref<number | string | null>(null);
    const trackAlbumId = ref<number | string | null>(null);
    const activeDescriptorSource = ref<PlaybackSourceSlug | null>(null);
    const mode = ref<PlaybackMode>('idle');

    const downloadFraction = ref(0);
    const objectUrlToRevoke = ref<string | null>(null);

    const currentTimeSeconds = ref(0);
    const durationSeconds = ref(0);
    const isPaused = ref(true);

    // ---- queue state -------------------------------------------------------

    /**
     * Always REPLACED, never mutated in place. That makes "did the queue
     * change" one identity comparison for watchers, and keeps Vue from deeply
     * proxying a `File` on a local-upload entry.
     */
    const queueEntries = shallowRef<QueueEntry[]>([]);
    const currentQueueIndex = ref(0);
    const repeatMode = ref<RepeatMode>('off');
    const shuffleEnabled = ref(false);
    /** Entry order before shuffle, so toggling off is reversible. */
    const preShuffleEntryIds = shallowRef<string[]>([]);
    /** Entries heard in this pass, so a shuffle never replays them first. */
    const playedEntryIds = shallowRef<Set<string>>(new Set());

    /** Size of the list the queue was built from, for "first 500 of 12,431". */
    const queueTotalCount = ref(0);
    const queueTruncated = ref(false);
    const queueLabel = ref<string | null>(null);

    let loadGeneration = 0;
    let consecutiveMissingEntries = 0;
    /**
     * Cancels the download feeding the PLAYING slot.
     *
     * The generation token is what makes a superseded load harmless, but it
     * cannot stop bytes already on the wire: ten rapid Next presses left ten
     * whole files racing each other for the connection, so the track the user
     * actually wanted arrived last. Every abort here is preceded by a generation
     * bump, so the resulting rejection always lands on a stale load and is
     * dropped rather than mistaken for a playback failure worth retrying.
     */
    let currentLoadAbortController: AbortController | null = null;
    /**
     * Pause pressed while the current track was still downloading.
     *
     * Nothing is playing yet at that moment, but the load path ends by calling
     * `el.play()` — so without this, a Pause from the lock screen was silently
     * outrun by the download it could not cancel. The load honours the flag by
     * attaching the finished track PAUSED; Play resumes from the loaded bytes.
     * Every new load clears it: a new load is a new play intent.
     */
    let pendingPauseDuringLoad = false;
    /**
     * The generation whose `play()` actually resolved, or null.
     *
     * This is what tells a genuine mid-track decode failure apart from the
     * `error` event our own teardown fires when it clears `src`. Teardown's
     * event is dispatched asynchronously, so it can land AFTER the next track's
     * source has been attached — at which point the element has a `currentSrc`
     * again and no amount of inspecting the element can tell the two apart.
     */
    let settledPlaybackGeneration: number | null = null;

    // ---- prefetch slot -----------------------------------------------------

    const prefetchObjectUrl = ref<string | null>(null);
    /** Which entry the prefetched blob belongs to. The key to consumption. */
    const prefetchEntryId = ref<string | null>(null);
    let prefetchGeneration = 0;
    let prefetchAbortController: AbortController | null = null;

    let hlsInstance: import('hls.js').default | null = null;

    const currentEntry = computed<QueueEntry | null>(
        () => queueEntries.value[currentQueueIndex.value] ?? null
    );

    const hasQueue = computed(() => queueEntries.value.length > 0);
    const canGoNext = computed(() => nextQueueIndex() !== null);
    const canGoPrevious = computed(() => queueEntries.value.length > 1 || currentTimeSeconds.value > 0);

    /**
     * Whether the failed current entry can be sent for an Audio File Issues deep
     * check. The bar ALSO requires the page grant before rendering the link —
     * never offer a link that 403s.
     */
    const currentEntrySupportsDeepCheck = computed(
        () => currentEntry.value !== null && supportsDeepCheck(currentEntry.value.identity)
    );

    /**
     * The bar's top identity line and the OS media controls' title: the track
     * title, else the code. One rule in one place — the lock screen showing a
     * different fallback than the bar reads as two different tracks.
     */
    const displayTitle = computed(() => trackTitle.value || trackCodeUpper.value || '');

    const subtitle = computed(() => {
        if (!activeDescriptorSource.value) return '';
        if (activeDescriptorSource.value === 'live_mp3') return 'Live (MP3)';
        if (activeDescriptorSource.value === 'live_hls') return 'Live (HLS)';
        if (activeDescriptorSource.value === 'ddex_mp3') return 'DDEX audio';
        if (activeDescriptorSource.value === 'manual_mp3') return 'Manual upload (preview)';
        return 'Original-bitrate backup';
    });

    function attachAudio(el: HTMLAudioElement | null) {
        // When the audio element goes away (e.g. the component that hosts the
        // reusable AudioPlayer unmounted, or HMR replaced it), stop playback but
        // KEEP the queue — a remount should come back to the same list, paused.
        // Destroying it here is what `close()` is for, and only the user's ✕
        // means that.
        if (el == null && audioEl.value != null) {
            suspend();
        }
        audioEl.value = el;
    }

    function syncTimelineFromDom() {
        const el = audioEl.value;
        if (!el) return;
        currentTimeSeconds.value = el.currentTime;
        durationSeconds.value = Number.isFinite(el.duration) && el.duration >= 0 ? el.duration : 0;
        isPaused.value = el.paused;
    }

    // ---- teardown ----------------------------------------------------------

    /**
     * Tear down what is PLAYING, leaving the prefetch slot alone.
     *
     * Every load path calls this one. It is deliberately not exported: the
     * prefetched blob has to survive an auto-advance, and that is exactly when
     * a load runs.
     */
    function stopCurrentPlayback() {
        const el = audioEl.value;
        settledPlaybackGeneration = null;
        currentLoadAbortController?.abort();
        currentLoadAbortController = null;
        if (objectUrlToRevoke.value) {
            URL.revokeObjectURL(objectUrlToRevoke.value);
            objectUrlToRevoke.value = null;
        }
        if (hlsInstance) {
            hlsInstance.destroy();
            hlsInstance = null;
        }
        if (el) {
            el.pause();
            el.removeAttribute('src');
            el.load();
        }
        mode.value = 'idle';
        downloadFraction.value = 0;
        currentTimeSeconds.value = 0;
        durationSeconds.value = 0;
        isPaused.value = true;
    }

    /** Drop the prefetched blob and abandon any download filling it. */
    function discardPrefetch() {
        prefetchGeneration += 1;
        prefetchAbortController?.abort();
        prefetchAbortController = null;
        if (prefetchObjectUrl.value) {
            URL.revokeObjectURL(prefetchObjectUrl.value);
            prefetchObjectUrl.value = null;
        }
        prefetchEntryId.value = null;
    }

    /**
     * Throw away EVERYTHING — both slots.
     *
     * The name stays total on purpose. It is exported and callable from
     * components, so a version that quietly spared the prefetch slot would leak
     * a whole MP3 per call for the next person who used it.
     */
    function teardownPlayback() {
        stopCurrentPlayback();
        discardPrefetch();
    }

    /**
     * Stop playing but keep the queue — the unmount/remount path.
     *
     * Bumps the load generation so an in-flight load for the element we just
     * dropped cannot come back and attach to the next one.
     */
    function suspend() {
        loadGeneration += 1;
        teardownPlayback();
    }

    function close() {
        loadGeneration += 1;
        teardownPlayback();
        visible.value = false;
        trackCodeUpper.value = null;
        trackTitle.value = null;
        trackArtist.value = null;
        trackAlbum.value = null;
        coverArtUrl.value = null;
        trackEntityId.value = null;
        trackRecordId.value = null;
        trackArtistId.value = null;
        trackAlbumId.value = null;
        activeDescriptorSource.value = null;
        errorMessage.value = null;
        // Close means close: the queue goes too.
        queueEntries.value = [];
        currentQueueIndex.value = 0;
        preShuffleEntryIds.value = [];
        playedEntryIds.value = new Set();
        shuffleEnabled.value = false;
        repeatMode.value = 'off';
        queueTotalCount.value = 0;
        queueTruncated.value = false;
        queueLabel.value = null;
        consecutiveMissingEntries = 0;
        // Synchronously, not via the persistence watcher: that flushes post-tick,
        // so closing the player and immediately reloading could otherwise race
        // and bring the queue back from the dead.
        clearPersistedQueue();
    }

    // ---- loading -----------------------------------------------------------

    async function prepareBlobFromStreamUrl(fullUrl: string, onProgress: ((fraction: number) => void) | null, signal?: AbortSignal): Promise<Blob> {
        const response = await http.get<ArrayBuffer>(fullUrl, {
            responseType: 'arraybuffer',
            signal,
            onDownloadProgress: (ev) => {
                const total = ev.total;
                if (total && total > 0) {
                    onProgress?.(Math.min(1, ev.loaded / total));
                }
            },
        });
        onProgress?.(1);

        return new Blob([response.data], { type: 'audio/mpeg' });
    }

    /** Mint a (fresh) descriptor for an entry, or null when it carries its own audio. */
    async function resolveDescriptor(entry: QueueEntry): Promise<AudioPlaybackDescriptor | null> {
        const identity = entry.identity;

        if (identity.kind === 'live_track') {
            // The original-bitrate backup has its own AFI-page-gated endpoint;
            // the live MP3 / HLS sources share the live-track descriptor route.
            if (entry.source === 'backup_mp3') {
                return fetchBackupPresignedDescriptor(identity.code);
            }

            return fetchPlaybackDescriptor(identity.code, entry.source === 'live_hls' ? 'live_hls' : 'live_mp3');
        }

        if (identity.kind === 'raw_ddex') {
            return fetchRawDdexTrackAudioDescriptor(identity.rawDdexTrackId);
        }

        if (identity.kind === 'fixed_descriptor') {
            return identity.descriptor;
        }

        // local_file — nothing to fetch, the bytes are already in memory.
        return null;
    }

    /**
     * Turn an entry + descriptor into something attachable.
     *
     * `onProgress` is a PARAMETER rather than a direct write to
     * `downloadFraction`, which is what lets prefetch reuse this exact path
     * without moving the visible download bar over the track that is playing.
     */
    async function preparePlayable(
        entry: QueueEntry,
        descriptor: AudioPlaybackDescriptor | null,
        onProgress: ((fraction: number) => void) | null,
        signal?: AbortSignal
    ): Promise<PreparedPlayable> {
        if (entry.identity.kind === 'local_file') {
            onProgress?.(1);

            return { kind: 'object_url', url: URL.createObjectURL(entry.identity.file) };
        }

        if (descriptor === null) {
            throw new Error('No audio source for this track.');
        }

        if (descriptor.kind === 'hls') {
            onProgress?.(1);

            return { kind: 'hls', url: descriptor.url };
        }

        if (descriptor.kind === 'url') {
            onProgress?.(0);
            // Cross-origin presigned URL (e.g. MinIO/S3 directly). Must use a clean axios
            // instance — the shared `http` carries Sanctum cookies / CSRF / auth headers
            // that MinIO rejects with HTTP 400.
            const presignedBlob = await downloadPresignedUrlAsBlob(
                descriptor.url,
                descriptor.content_length ?? null,
                (loaded, total) => {
                    if (total != null && total > 0) {
                        onProgress?.(Math.min(1, loaded / total));
                    }
                },
                signal
            );
            onProgress?.(1);

            const blob =
                presignedBlob.type !== ''
                    ? presignedBlob
                    : new Blob([presignedBlob], { type: descriptor.mime ?? 'audio/mpeg' });

            return { kind: 'object_url', url: URL.createObjectURL(blob) };
        }

        onProgress?.(0);
        const blob = await prepareBlobFromStreamUrl(resolvePlaybackUrl(descriptor.url), onProgress, signal);

        return { kind: 'object_url', url: URL.createObjectURL(blob) };
    }

    /** Hand a prepared source to the element. Assumes the playing slot is already clear. */
    /**
     * Hand a prepared source to the element. Assumes the playing slot is clear.
     *
     * Takes the generation because of the dynamic import below — the only await
     * in the load path that was not followed by a generation check. A superseded
     * load would resume here and bind ITS stream to the element the next track is
     * already using: Option-click Play HLS (first use in a session, so the chunk
     * is a real network fetch), press Next before it lands, and the dead load
     * wins. Checked before anything is constructed, so a stale load leaves no Hls
     * instance behind either.
     */
    async function attachPrepared(prepared: PreparedPlayable, generation: number): Promise<void> {
        const el = audioEl.value;
        if (!el) {
            throw new Error('Audio element not ready');
        }

        if (prepared.kind === 'hls') {
            const HlsLib = await import('hls.js').then((m) => m.default);
            if (generation !== loadGeneration) {
                return;
            }

            mode.value = 'hls';

            if (HlsLib.isSupported()) {
                hlsInstance = new HlsLib();
                hlsInstance.loadSource(prepared.url);
                hlsInstance.attachMedia(el);
            } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
                el.src = prepared.url;
            } else {
                throw new Error('HLS playback is not supported in this browser.');
            }
            downloadFraction.value = 1;

            return;
        }

        objectUrlToRevoke.value = prepared.url;
        el.src = prepared.url;
        mode.value = 'mp3_ready';
    }

    function applyEntryIdentity(entry: QueueEntry) {
        trackCodeUpper.value = entry.code;
        trackTitle.value = entry.title;
        trackArtist.value = entry.artist;
        trackAlbum.value = entry.album;
        coverArtUrl.value = entry.coverUrl;
        // Carried on the entry, so skipping to the next track re-points the
        // bar's links at THAT track rather than leaving the previous one's.
        trackEntityId.value = entry.trackEntityId;
        trackRecordId.value = entry.trackRecordId;
        trackArtistId.value = entry.artistId;
        trackAlbumId.value = entry.albumId;
        activeDescriptorSource.value = entry.source;
    }

    function markEntryFailure(entryId: string, reason: string | null) {
        queueEntries.value = queueEntries.value.map((entry) =>
            entry.entryId === entryId ? { ...entry, failureReason: reason } : entry
        );
    }

    /**
     * Load and play the entry at `index`.
     *
     * Throws on failure, so the public `loadAndPlay*` wrappers keep their
     * reject-on-failure contract. Internal callers (auto-advance, next/previous)
     * catch and stop.
     */
    async function playEntryAtIndex(
        index: number,
        options: { allowRetry?: boolean; viaAutoAdvance?: boolean } = {}
    ): Promise<void> {
        const allowRetry = options.allowRetry ?? true;
        // Whether we reached this entry on our own (end of the previous track,
        // or walking past entries that no longer exist) rather than because the
        // user picked it. Only the former may drop a missing track quietly.
        const viaAutoAdvance = options.viaAutoAdvance ?? false;
        const entry = queueEntries.value[index];
        if (!entry) {
            return;
        }

        // A missing element is a client-side precondition, not a transient
        // failure — it is checked before any network work and is never retried.
        if (!audioEl.value) {
            errorMessage.value = 'Audio element not ready';
            mode.value = 'idle';
            throw new Error('Audio element not ready');
        }

        const generation = ++loadGeneration;
        pendingPauseDuringLoad = false;
        currentQueueIndex.value = index;
        applyEntryIdentity(entry);
        errorMessage.value = null;
        if (entry.failureReason !== null) {
            markEntryFailure(entry.entryId, null);
        }

        try {
            // Take BEFORE tearing the playing slot down, assign AFTER: at no
            // instant does one URL sit in both slots, and the teardown that runs
            // on auto-advance never sees the blob it must not revoke.
            const promotedUrl = takePrefetchedFor(entry.entryId);

            stopCurrentPlayback();

            if (promotedUrl !== null) {
                mode.value = 'mp3_ready';
                downloadFraction.value = 1;
                objectUrlToRevoke.value = promotedUrl;
                audioEl.value.src = promotedUrl;
            } else {
                mode.value = 'mp3_loading';
                downloadFraction.value = 0;

                // Created AFTER the teardown above, which aborts the previous
                // load's download — otherwise this one would abort itself.
                const loadAbortController = new AbortController();
                currentLoadAbortController = loadAbortController;

                const descriptor = await resolveDescriptor(entry);
                if (generation !== loadGeneration) return;

                const prepared = await preparePlayable(
                    entry,
                    descriptor,
                    (fraction) => {
                        if (generation === loadGeneration) {
                            downloadFraction.value = fraction;
                        }
                    },
                    loadAbortController.signal
                );
                if (generation !== loadGeneration) {
                    if (prepared.kind === 'object_url') {
                        URL.revokeObjectURL(prepared.url);
                    }

                    return;
                }

                await attachPrepared(prepared, generation);
                if (generation !== loadGeneration) return;
            }

            if (pendingPauseDuringLoad) {
                // Attached and ready, but Pause arrived mid-download. Leave it
                // paused; Play resumes instantly from the downloaded audio.
                pendingPauseDuringLoad = false;
            } else {
                await audioEl.value.play();
                if (generation !== loadGeneration) return;
            }

            settledPlaybackGeneration = generation;
            syncTimelineFromDom();
            consecutiveMissingEntries = 0;
            playedEntryIds.value = new Set(playedEntryIds.value).add(entry.entryId);
            void maybePrefetchNext();
        } catch (err: unknown) {
            if (generation !== loadGeneration) {
                return;
            }

            // The boring, self-healing case: a presigned URL that expired
            // between being minted and being used. Re-resolve once and retry.
            if (allowRetry && canReresolveIdentity(entry.identity) && !isMissingResourceError(err)) {
                return playEntryAtIndex(index, { allowRetry: false, viaAutoAdvance });
            }

            // A track that is GONE is not a broken track — most often a queue
            // restored from an earlier session whose catalogue has moved on. Drop
            // it and carry on rather than stopping the queue on it.
            //
            // ONLY when we got here by advancing on our own. A track the user
            // explicitly asked for — clicked, jumped to, or skipped to — fails
            // loudly, because silently playing something else is precisely the
            // "no silent skipping" the failure spec rules out. This also keeps
            // loadAndPlay*'s reject-on-failure contract intact.
            if (isMissingResourceError(err) && viaAutoAdvance && queueEntries.value.length > 1) {
                consecutiveMissingEntries += 1;

                // Re-derive the position rather than reusing the `index` captured
                // before the download: a reorder or a removal while this entry
                // was loading makes that number point at a different track, and
                // every decision below is about where this one sat.
                const liveIndex = queueEntries.value.findIndex(
                    (candidate) => candidate.entryId === entry.entryId
                );
                if (liveIndex === -1) {
                    // Removed while it loaded — there is nothing left to drop.
                    return;
                }

                const remaining = queueEntries.value.filter(
                    (candidate) => candidate.entryId !== entry.entryId
                );
                queueEntries.value = remaining;

                // Bounded, so a queue restored after a catalogue purge fires ten
                // requests and stops rather than five hundred silent ones.
                if (consecutiveMissingEntries >= MAX_CONSECUTIVE_MISSING_ENTRIES) {
                    consecutiveMissingEntries = 0;
                    errorMessage.value =
                        'Stopped: several tracks in a row are no longer available.';
                    mode.value = 'idle';

                    return;
                }

                // The dropped entry's slot is now filled by whatever followed it.
                // When nothing did, the queue has ENDED here — `index` is past
                // the end of what remains, and clamping it back into range would
                // land on the track that just finished and play it a second
                // time. That is not a quiet drop, it is a wrong one.
                const droppedEntryWasLast = liveIndex >= remaining.length;

                if (droppedEntryWasLast && repeatMode.value !== 'all') {
                    currentQueueIndex.value = remaining.length - 1;
                    applyEntryIdentity(remaining[remaining.length - 1]);
                    mode.value = 'idle';

                    return;
                }

                const nextIndex = droppedEntryWasLast ? 0 : liveIndex;

                // Still advancing on our own, so the cascade stays bounded and quiet.
                return playEntryAtIndex(nextIndex, { viaAutoAdvance: true });
            }

            const message = errorText(err, 'Playback failed.');
            errorMessage.value = message;
            // Leave no consumer stuck on a loading state: a failed load means nothing
            // is playable, so drop back to idle (spinners key off mode === 'mp3_loading').
            mode.value = 'idle';
            markEntryFailure(entry.entryId, message);
            throw err;
        }
    }

    // ---- prefetch ----------------------------------------------------------

    /**
     * The ONLY way a URL leaves the prefetch slot without being revoked.
     *
     * Keyed on `entryId`: a blob fetched for an entry that is no longer the one
     * being played is revoked here rather than handed over, so a stale slot
     * costs memory, never correctness.
     */
    function takePrefetchedFor(entryId: string): string | null {
        const url = prefetchObjectUrl.value;
        if (url === null) {
            // Nothing to promote. If a prefetch of THIS entry is still
            // downloading, it has just been overtaken: the caller is about to
            // fetch the same file into the playing slot. Leaving it running
            // pulled the whole track down twice AND kept `maybePrefetchNext`
            // blocked, so the entry after this one never got prefetched at all —
            // pressing Next turned the feature off exactly when it was wanted.
            if (prefetchEntryId.value === entryId) {
                discardPrefetch();
            }

            return null;
        }

        if (prefetchEntryId.value !== entryId) {
            discardPrefetch();

            return null;
        }

        prefetchGeneration += 1;
        prefetchAbortController = null;
        prefetchObjectUrl.value = null;
        prefetchEntryId.value = null;

        return url;
    }

    /** Index auto-advance would play next, or null when the queue ends here. */
    function nextQueueIndex(): number | null {
        const entries = queueEntries.value;
        if (entries.length === 0) {
            return null;
        }

        const next = currentQueueIndex.value + 1;
        if (next < entries.length) {
            return next;
        }

        if (repeatMode.value === 'all') {
            return 0;
        }

        return null;
    }

    /** Which entry a prefetch should be holding right now, if any. */
    function prefetchTargetEntry(): QueueEntry | null {
        // Repeat-one replays the element it already has — prefetching would hold
        // a second copy of the file that is playing.
        if (repeatMode.value === 'one') {
            return null;
        }

        const index = nextQueueIndex();
        if (index === null) {
            return null;
        }

        const entry = queueEntries.value[index] ?? null;
        if (entry === null) {
            return null;
        }

        // Never the entry that is playing — the element already holds those
        // bytes. Reachable today via repeat-all on a one-entry queue, where
        // "next" is where we already are, which downloaded a second copy of the
        // same file and held two object URLs for identical bytes. Keyed on
        // entryId so the rule states the invariant, not the one case.
        if (entry.entryId === currentEntry.value?.entryId) {
            return null;
        }

        // HLS streams itself; a local file is already in memory. Neither has a
        // blob worth fetching ahead.
        if (entry.identity.kind === 'local_file' || entry.source === 'live_hls') {
            return null;
        }

        return entry;
    }

    async function maybePrefetchNext(): Promise<void> {
        const target = prefetchTargetEntry();
        if (target === null) {
            return;
        }

        // Already holding this entry, or already downloading it.
        if (prefetchEntryId.value === target.entryId) {
            return;
        }

        // A download for some OTHER entry is still running. It is no longer
        // wanted, and it must not be left to block this one — that is how a
        // stale prefetch used to stop every later prefetch from starting.
        if (prefetchAbortController !== null) {
            discardPrefetch();
        }

        const generation = ++prefetchGeneration;
        const abortController = new AbortController();
        prefetchAbortController = abortController;
        prefetchEntryId.value = target.entryId;

        try {
            const descriptor = await resolveDescriptor(target);
            if (generation !== prefetchGeneration) return;

            const prepared = await preparePlayable(target, descriptor, null, abortController.signal);
            if (generation !== prefetchGeneration || prepared.kind !== 'object_url') {
                if (prepared.kind === 'object_url') {
                    URL.revokeObjectURL(prepared.url);
                }

                return;
            }

            prefetchObjectUrl.value = prepared.url;
        } catch {
            // Prefetch is an optimisation. The real attempt at advance time is
            // what reports, so a failure here stays quiet rather than surfacing
            // an error for a track the user has not asked for yet.
            if (generation === prefetchGeneration) {
                prefetchEntryId.value = null;
            }
        } finally {
            if (prefetchAbortController === abortController) {
                prefetchAbortController = null;
            }
        }
    }

    /**
     * Re-point the prefetch slot after the queue changed.
     *
     * Deliberately compares the TARGET rather than discarding unconditionally:
     * a reorder that doesn't change what plays next should keep the blob it
     * already paid for.
     */
    function reconcilePrefetch() {
        const target = prefetchTargetEntry();
        const targetId = target?.entryId ?? null;

        if (prefetchEntryId.value === targetId) {
            return;
        }

        discardPrefetch();
        void maybePrefetchNext();
    }

    // ---- queue mutation ----------------------------------------------------

    function replaceQueue(entries: QueueEntry[], meta: { total?: number; truncated?: boolean; label?: string | null } = {}) {
        discardPrefetch();
        queueEntries.value = entries;
        currentQueueIndex.value = 0;
        playedEntryIds.value = new Set();
        preShuffleEntryIds.value = [];
        shuffleEnabled.value = false;
        // A new list is a new listening session. Inherited repeat-one made a
        // one-off play from the Audio File Issues inspector loop forever with no
        // queue UI on screen to explain it — and it is what made the repeat-all
        // prefetch case reachable at all.
        repeatMode.value = 'off';
        queueTotalCount.value = meta.total ?? entries.length;
        queueTruncated.value = meta.truncated ?? false;
        queueLabel.value = meta.label ?? null;
        consecutiveMissingEntries = 0;
    }

    /**
     * Forget which list this queue was built from.
     *
     * The label and the total describe a SOURCE — "Custom Playlist - 90s Alt
     * Rock", "First 500 of 12,431". Both stop being true the moment the queue
     * stops being exactly that list: append a bucket track and the label names
     * the wrong playlist; remove a row and those 499 are not the first 499
     * either. Reordering is deliberately NOT one of these, because dragging rows
     * around changes the order the user asked for, not which list this is.
     */
    function detachQueueFromItsSource() {
        queueLabel.value = null;
        queueTruncated.value = false;
        queueTotalCount.value = queueEntries.value.length;
    }

    /** Append one entry. Starts playing it when nothing is playing. */
    async function enqueue(entry: QueueEntry, maxEntries = QUEUE_MAX_ENTRIES): Promise<boolean> {
        if (queueEntries.value.length >= maxEntries) {
            return false;
        }

        const wasEmpty = queueEntries.value.length === 0;
        queueEntries.value = [...queueEntries.value, entry];
        visible.value = true;

        detachQueueFromItsSource();

        if (wasEmpty) {
            // A Shift-click into silence that appended and did nothing would
            // read as a broken button.
            currentQueueIndex.value = 0;
            await playEntryAtIndex(0).catch(() => undefined);

            return true;
        }

        reconcilePrefetch();

        return true;
    }

    async function jumpToEntry(entryId: string): Promise<void> {
        const index = queueEntries.value.findIndex((entry) => entry.entryId === entryId);
        if (index === -1) {
            return;
        }

        // Jumping to the entry that is ALREADY playing is a pause, not a jump.
        // `playEntryAtIndex` has no such notion — it stops playback and
        // re-resolves the descriptor from scratch — so three minutes into a
        // track, clicking its own row restarted it from 0:00 and re-downloaded
        // the audio. The cover's play button got this right on its own, which
        // left one row with a small target that paused and a much larger one
        // that reloaded. Here rather than in the queue view so every caller
        // gets it.
        if (index === currentQueueIndex.value && mode.value !== 'idle') {
            await togglePlay();

            return;
        }

        await playEntryAtIndex(index).catch(() => undefined);
    }

    async function removeEntry(entryId: string): Promise<void> {
        const entries = queueEntries.value;
        const index = entries.findIndex((entry) => entry.entryId === entryId);
        if (index === -1) {
            return;
        }

        const wasCurrent = index === currentQueueIndex.value;
        const remaining = entries.filter((entry) => entry.entryId !== entryId);
        queueEntries.value = remaining;

        if (remaining.length === 0) {
            close();

            return;
        }

        detachQueueFromItsSource();

        if (!wasCurrent) {
            // Keep the same entry current by id, not by position.
            if (index < currentQueueIndex.value) {
                currentQueueIndex.value -= 1;
            }
            reconcilePrefetch();

            return;
        }

        // Removing what is playing advances into the slot it left behind.
        const nextIndex = Math.min(index, remaining.length - 1);
        await playEntryAtIndex(nextIndex).catch(() => undefined);
    }

    /** Reorder from a list of entry ids (what the drag view emits). */
    function reorderQueue(entryIds: string[]): void {
        const byId = new Map(queueEntries.value.map((entry) => [entry.entryId, entry]));
        const reordered: QueueEntry[] = [];
        for (const entryId of entryIds) {
            const entry = byId.get(entryId);
            if (entry) {
                reordered.push(entry);
                byId.delete(entryId);
            }
        }

        // A drag emit that isn't a permutation of what we hold is ignored rather
        // than applied half-way — the view re-syncs from the store.
        if (reordered.length !== queueEntries.value.length || byId.size !== 0) {
            return;
        }

        const currentEntryId = currentEntry.value?.entryId ?? null;
        queueEntries.value = reordered;
        if (currentEntryId !== null) {
            const seatedIndex = reordered.findIndex((entry) => entry.entryId === currentEntryId);
            currentQueueIndex.value = seatedIndex === -1 ? 0 : seatedIndex;
        }

        reconcilePrefetch();
    }

    /** Drop everything after the current entry, leaving it playing. */
    function clearUpcoming(): void {
        queueEntries.value = queueEntries.value.slice(0, currentQueueIndex.value + 1);
        detachQueueFromItsSource();
        reconcilePrefetch();
    }

    function shuffleArray<T>(items: T[], random: () => number): T[] {
        const shuffled = [...items];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(random() * (i + 1));
            const swap = shuffled[i];
            shuffled[i] = shuffled[j];
            shuffled[j] = swap;
        }

        return shuffled;
    }

    /**
     * Shuffle the UPCOMING entries, or restore the pre-shuffle order.
     *
     * Upcoming entries are partitioned into unheard and already-heard, and only
     * the unheard are shuffled to the front — so a shuffle after a backward jump
     * doesn't replay something you just heard before things you haven't.
     */
    function setShuffle(enabled: boolean, random: () => number = Math.random): void {
        if (enabled === shuffleEnabled.value) {
            return;
        }

        const entries = queueEntries.value;
        const currentEntryId = currentEntry.value?.entryId ?? null;

        if (enabled) {
            preShuffleEntryIds.value = entries.map((entry) => entry.entryId);

            const head = entries.slice(0, currentQueueIndex.value + 1);
            const tail = entries.slice(currentQueueIndex.value + 1);
            const heard = playedEntryIds.value;
            const unheardTail = tail.filter((entry) => !heard.has(entry.entryId));
            const heardTail = tail.filter((entry) => heard.has(entry.entryId));

            queueEntries.value = [...head, ...shuffleArray(unheardTail, random), ...heardTail];
        } else {
            const byId = new Map(entries.map((entry) => [entry.entryId, entry]));
            const restored: QueueEntry[] = [];
            for (const entryId of preShuffleEntryIds.value) {
                const entry = byId.get(entryId);
                if (entry) {
                    restored.push(entry);
                    byId.delete(entryId);
                }
            }
            // Anything appended while shuffled has no recorded position, so it
            // lands at the end rather than being dropped.
            for (const entry of entries) {
                if (byId.has(entry.entryId)) {
                    restored.push(entry);
                }
            }

            queueEntries.value = restored;
            preShuffleEntryIds.value = [];
        }

        shuffleEnabled.value = enabled;

        if (currentEntryId !== null) {
            const seatedIndex = queueEntries.value.findIndex((entry) => entry.entryId === currentEntryId);
            currentQueueIndex.value = seatedIndex === -1 ? 0 : seatedIndex;
        }

        reconcilePrefetch();
    }

    function cycleRepeatMode(): void {
        const nextMode: Record<RepeatMode, RepeatMode> = { off: 'all', all: 'one', one: 'off' };
        repeatMode.value = nextMode[repeatMode.value];
        reconcilePrefetch();
    }

    // ---- transport ---------------------------------------------------------

    async function playNext(options: { viaAutoAdvance?: boolean } = {}): Promise<void> {
        const index = nextQueueIndex();
        if (index === null) {
            return;
        }

        await playEntryAtIndex(index, options).catch(() => undefined);
    }

    async function playPrevious(): Promise<void> {
        const el = audioEl.value;
        // The behaviour every music player has trained people to expect: past a
        // few seconds, Previous restarts the track rather than leaving it.
        if (el && el.currentTime > PREVIOUS_RESTARTS_ABOVE_SECONDS) {
            el.currentTime = 0;
            syncTimelineFromDom();

            return;
        }

        const previousIndex = currentQueueIndex.value - 1;
        if (previousIndex < 0) {
            if (el) {
                el.currentTime = 0;
                syncTimelineFromDom();
            }

            return;
        }

        await playEntryAtIndex(previousIndex).catch(() => undefined);
    }

    /** `@ended` on the audio element. */
    async function onTrackEnded(): Promise<void> {
        syncTimelineFromDom();

        if (repeatMode.value === 'one') {
            const el = audioEl.value;
            if (el) {
                el.currentTime = 0;
                await el.play().catch(() => undefined);
                syncTimelineFromDom();
            }

            return;
        }

        // The one path that reached this entry on its own.
        await playNext({ viaAutoAdvance: true });
    }

    /**
     * `@error` on the audio element.
     *
     * `el.play()` resolves as soon as playback STARTS, so a decode failure two
     * seconds in never reaches the load path's catch. Without this the track
     * would sit silently at 0:00 looking fine.
     *
     * Guarded on the load having SETTLED rather than on the element looking
     * healthy. Clearing `src` fires `error` too, and that event is dispatched
     * asynchronously — so it can arrive after the NEXT track's source has been
     * attached, at which point `currentSrc` is non-empty again and the element
     * is indistinguishable from one that genuinely failed. Anchoring to the
     * generation whose `play()` resolved tells them apart: teardown clears it,
     * and the incoming load does not set it until that track is really playing.
     */
    function onAudioElementError(): void {
        const el = audioEl.value;
        if (!el || mode.value === 'idle' || settledPlaybackGeneration !== loadGeneration) {
            return;
        }

        const message = 'This track could not be played.';
        errorMessage.value = message;
        mode.value = 'idle';
        if (currentEntry.value) {
            markEntryFailure(currentEntry.value.entryId, message);
        }
    }

    // ---- public play entry points -----------------------------------------

    /**
     * Re-source the entry that is already playing, keeping the queue.
     *
     * Option-clicking the bar switches the current track to HLS by calling
     * `loadAndPlay` with the code that is already loaded. Under "a one-off play
     * replaces the queue" that would throw away everything queued behind it.
     */
    function isSourceSwitchForCurrentEntry(code: string, position: number | null = null): boolean {
        const entry = currentEntry.value;
        if (entry === null || queueEntries.value.length <= 1) {
            return false;
        }

        // The occurrence, not just the track. Matching on code alone meant that
        // Option-clicking the THIRD row of a track a playlist names three times
        // re-sourced the FIRST one — the occurrence that happened to be playing.
        return isSameQueueOccurrence({ code, position }, entry);
    }

    /**
     * Play one already-built entry as a queue of one.
     *
     * The entry point for callers that know an entry's IDENTITY (a raw DDEX id, a
     * live track code) rather than holding a descriptor they fetched themselves —
     * the store does the resolving, so it happens exactly once and the entry stays
     * re-resolvable on retry and across a reload.
     */
    async function loadAndPlayEntry(entry: QueueEntry): Promise<void> {
        errorMessage.value = null;
        visible.value = true;

        replaceQueue([entry]);
        await playEntryAtIndex(0);
    }

    async function loadAndPlayWithDescriptor(
        options: PlaybackDisplayOptions & {
            source: PlaybackSourceSlug;
            descriptor: AudioPlaybackDescriptor;
        }
    ) {
        errorMessage.value = null;
        visible.value = true;

        const entry = buildQueueEntry({
            // The caller already holds the descriptor and we have no id to re-mint
            // it from, so this entry is deliberately neither retryable nor
            // persistable. Callers that DO know the identity should use
            // loadAndPlayEntry instead of fetching a descriptor themselves.
            identity: { kind: 'fixed_descriptor', descriptor: options.descriptor },
            source: options.source,
            code: options.code,
            title: options.title,
            artist: options.artist,
            album: options.album,
            coverUrl: options.coverUrl,
            ...recordLinksFrom(options),
        });

        replaceQueue([entry]);
        await playEntryAtIndex(0);
    }

    async function loadAndPlayLocalFile(
        options: PlaybackDisplayOptions & { file: File }
    ): Promise<void> {
        errorMessage.value = null;
        visible.value = true;

        const entry = buildQueueEntry({
            identity: { kind: 'local_file', file: options.file },
            source: 'manual_mp3',
            code: options.code,
            title: options.title,
            artist: options.artist,
            album: options.album,
            coverUrl: options.coverUrl,
            ...recordLinksFrom(options),
        });

        replaceQueue([entry]);
        await playEntryAtIndex(0);
    }

    async function loadAndPlay(
        options: PlaybackDisplayOptions & {
            source: 'live_mp3' | 'live_hls' | 'backup_mp3';
            /**
             * The playlist numbering of the row that was clicked, when it has one.
             * Callers that ARE "the thing playing" (the bar, the Audio File Issues
             * inspector) pass nothing and get the current entry, which is right.
             */
            position?: number | null;
        }
    ) {
        errorMessage.value = null;
        visible.value = true;

        if (isSourceSwitchForCurrentEntry(options.code, options.position ?? null)) {
            const index = currentQueueIndex.value;
            queueEntries.value = queueEntries.value.map((entry, entryIndex) =>
                entryIndex === index ? { ...entry, source: options.source } : entry
            );
            discardPrefetch();
            await playEntryAtIndex(index);

            return;
        }

        const entry = buildQueueEntry({
            identity: { kind: 'live_track', code: options.code },
            source: options.source,
            code: options.code,
            title: options.title,
            artist: options.artist,
            album: options.album,
            coverUrl: options.coverUrl,
            position: options.position ?? null,
            ...recordLinksFrom(options),
        });

        replaceQueue([entry]);
        await playEntryAtIndex(0);
    }

    /**
     * Play a track and adopt the list it came from as the queue.
     *
     * Plays the clicked track FIRST, then fills the queue in around it. The
     * alternative — await the list, then play — puts a network round trip in
     * front of every play gesture and turns an endpoint outage into a dead Play
     * button. Returns false when the list could not be loaded, so the caller can
     * say so while the audio keeps playing.
     */
    async function playListInContext(
        entry: QueueEntry,
        loadList: () => Promise<{
            entries: QueueEntry[];
            total: number;
            truncated: boolean;
            label: string | null;
        }>
    ): Promise<boolean> {
        visible.value = true;
        replaceQueue([entry]);
        await playEntryAtIndex(0).catch(() => undefined);

        let list: Awaited<ReturnType<typeof loadList>>;
        try {
            list = await loadList();
        } catch {
            return false;
        }

        const playingEntry = currentEntry.value;
        if (playingEntry === null || playingEntry.entryId !== entry.entryId) {
            // The user moved on while the list was in flight — respect that
            // rather than yanking them back.
            return true;
        }

        // The same goes for a queue the user changed while the list was in
        // flight. Before the list arrives the queue holds the seed alone. If it
        // holds anything else now, the user built it, for example by
        // Shift-clicking a track to append it. Adopting the list here used to
        // throw that away and refill the queue with the whole result set.
        // Entry ids are compared, not objects, because marking a failure
        // replaces the queue with copies of the same entries.
        const queueIsStillTheSeed =
            queueEntries.value.length === 1 && queueEntries.value[0]?.entryId === entry.entryId;
        if (!queueIsStillTheSeed) {
            return true;
        }

        // Splice the LIVE entry object into its own slot so the track that is
        // already playing is not torn down and reloaded.
        //
        // `code` alone does not identify a row: a generated playlist may name
        // the same track three times, and matching on code found the first of
        // them every time — so clicking the third occurrence started the queue
        // at the top. When both sides carry the playlist's own numbering, that
        // is what says which occurrence was clicked.
        const isSameTrack = (candidate: QueueEntry): boolean =>
            candidate.code === entry.code && candidate.identity.kind === entry.identity.kind;

        let matchIndex = -1;

        if (entry.position !== null) {
            matchIndex = list.entries.findIndex(
                (candidate) => isSameTrack(candidate) && candidate.position === entry.position
            );
        }

        // No numbering on this list (a search result set, a bucket), or the
        // clicked position is not in the response — fall back to the code.
        if (matchIndex === -1) {
            matchIndex = list.entries.findIndex(isSameTrack);
        }

        // `playingEntry`, NOT the `entry` parameter. markEntryFailure replaces
        // the queue array with copies, so by now the parameter is the pre-play
        // object — splicing it put `failureReason: null` back over the row that
        // had just failed, leaving the bar showing an error the queue view did
        // not. It also reverted an Option-HLS source switch made while the list
        // was in flight. The match above still reads `entry`, whose code /
        // kind / position are immutable.
        // The live entry keeps everything it has EXCEPT the card, which it takes
        // from the list row it matched.
        //
        // The seed is built from a Play button's props — a handful of scalars —
        // so it has no card, while every entry the list loaded does. Splicing it
        // in unchanged left exactly one bare row in the queue: the track that was
        // playing. A shallow copy is safe here because `entryId` comes along, and
        // that is what the prefetch slot and `playedEntryIds` are keyed on.
        const withListCard = (entry: QueueEntry, listEntry: QueueEntry | undefined): QueueEntry =>
            listEntry?.card ? { ...entry, card: listEntry.card } : entry;

        const adopted =
            matchIndex === -1
                // The list drifted between click and response (a re-index, a
                // filter change). Keep the clicked track at the front rather
                // than restarting audio. No match means no card to adopt — the
                // queue renders its plain row for this one entry, which is
                // honest: we could not find it in the list we just loaded.
                ? [playingEntry, ...list.entries]
                : [
                      ...list.entries.slice(0, matchIndex),
                      withListCard(playingEntry, list.entries[matchIndex]),
                      ...list.entries.slice(matchIndex + 1),
                  ];

        queueEntries.value = adopted;
        currentQueueIndex.value = matchIndex === -1 ? 0 : matchIndex;
        playedEntryIds.value = new Set([entry.entryId]);
        queueTotalCount.value = list.total;
        queueTruncated.value = list.truncated;
        queueLabel.value = list.label;

        reconcilePrefetch();

        return true;
    }

    /**
     * Start (or resume) playback — the half of the pair the OS "play" action
     * maps onto.
     *
     * Decides off `mode` and the ELEMENT's own `paused`, never the `isPaused`
     * mirror: error paths leave the mirror stale (a decode error fires `error`
     * but no `pause`, so nothing resets it), and a control that trusted it
     * could reload a failed track when the user asked to pause.
     */
    async function play(): Promise<void> {
        const el = audioEl.value;
        if (!el) return;

        // The download in flight already ends by starting playback. Calling
        // `el.play()` NOW would run on an element whose src was removed by
        // stopCurrentPlayback: the glyph flips to "playing" over nothing and
        // the rejection lands unhandled. Just cancel any pending pause.
        if (mode.value === 'mp3_loading') {
            pendingPauseDuringLoad = false;

            return;
        }

        // A queue restored from localStorage comes back with no audio loaded.
        // Play means "load the current entry and start", not "resume nothing".
        if (mode.value === 'idle') {
            if (currentEntry.value !== null) {
                await playEntryAtIndex(currentQueueIndex.value).catch(() => undefined);
            }

            return;
        }

        if (el.paused) {
            await el.play();
        }
        syncTimelineFromDom();
    }

    /** Stop playback — the half the OS "pause" action maps onto. */
    function pause(): void {
        const el = audioEl.value;
        if (!el) return;

        // Nothing is playing yet, but the download will end in `el.play()`.
        // Record the intent so the finished track attaches paused instead.
        if (mode.value === 'mp3_loading') {
            pendingPauseDuringLoad = true;
            isPaused.value = true;

            return;
        }

        if (!el.paused) {
            el.pause();
        }
        syncTimelineFromDom();
    }

    async function togglePlay() {
        const el = audioEl.value;
        if (!el) return;

        // The element, not the isPaused mirror — see play()'s doc comment.
        if (el.paused) {
            await play();
        } else {
            pause();
        }
    }

    // ---- persistence -------------------------------------------------------

    /**
     * Rebuild the queue from localStorage, paused and with no audio loaded.
     *
     * Called explicitly by `AudioPlayer.vue` on mount rather than at store
     * construction, so a test (or any consumer) that never wants it can simply
     * not call it — and so it is the obvious seam if this ever becomes a
     * server-side cross-device queue.
     */
    function restoreFromPersistence(): void {
        if (queueEntries.value.length > 0) {
            return;
        }

        const snapshot = readPersistedQueue();
        if (snapshot === null) {
            return;
        }

        const entries = snapshot.entries.map<QueueEntry>((persisted) => ({
            entryId: persisted.entryId,
            identity: persisted.identity,
            source: persisted.source,
            code: persisted.code,
            title: persisted.title,
            artist: persisted.artist,
            album: persisted.album,
            // Cover art is a signed URL and was deliberately not persisted.
            coverUrl: null,
            // Nor is position: it only ever matters while a list is being
            // adopted around a clicked track, which has long since happened.
            position: null,
            // The record links DO survive: they are plain ids, they don't
            // expire the way a signed cover URL does, and a restored bar whose
            // lines had stopped navigating would read as broken.
            trackEntityId: persisted.trackEntityId,
            trackRecordId: persisted.trackRecordId,
            artistId: persisted.artistId,
            albumId: persisted.albumId,
            failureReason: null,
            // The card DOES come back. Dropping it made a reload silently
            // downgrade every row to the bare fallback and left the density
            // toggle doing nothing, with no way to recover short of re-playing
            // the list. Its cover URL was stripped on write because that one
            // field expires; the card falls back to the placeholder art, which
            // is what an entry without a cover has always done.
            card: persisted.card,
        }));

        queueEntries.value = entries;
        const restoredIndex = entries.findIndex((entry) => entry.entryId === snapshot.currentEntryId);
        currentQueueIndex.value = restoredIndex === -1 ? 0 : restoredIndex;
        repeatMode.value = snapshot.repeatMode === 'all' || snapshot.repeatMode === 'one' ? snapshot.repeatMode : 'off';

        // Shuffle is only ON if we can turn it OFF again. A snapshot written
        // before the order was persisted — every queue on every machine today —
        // has the flag but no order, and trusting it produced a lit button that
        // did nothing when pressed. Restoring as off is truthful and reversible.
        const restoredOrder = snapshot.preShuffleEntryIds;
        const orderCoversQueue =
            restoredOrder.length > 0 &&
            entries.every((entry) => restoredOrder.includes(entry.entryId));

        shuffleEnabled.value = snapshot.shuffleEnabled && orderCoversQueue;
        preShuffleEntryIds.value = shuffleEnabled.value ? restoredOrder : [];
        queueTotalCount.value = entries.length;
        queueTruncated.value = false;

        // Comes back paused with nothing loaded: pressing play is what fetches a
        // descriptor, so a reload never re-downloads audio nobody asked for.
        mode.value = 'idle';
        isPaused.value = true;
        visible.value = true;
        applyEntryIdentity(entries[currentQueueIndex.value]);
    }

    watch(
        [queueEntries, currentQueueIndex, repeatMode, shuffleEnabled],
        () => {
            if (queueEntries.value.length === 0) {
                clearPersistedQueue();

                return;
            }

            writePersistedQueue({
                entries: queueEntries.value,
                currentEntryId: currentEntry.value?.entryId ?? null,
                repeatMode: repeatMode.value,
                shuffleEnabled: shuffleEnabled.value,
                preShuffleEntryIds: preShuffleEntryIds.value,
            });
        },
        { flush: 'post' }
    );

    function seekToFraction(fraction: number) {
        const el = audioEl.value;
        if (!el || !Number.isFinite(fraction)) return;
        const f = Math.min(1, Math.max(0, fraction));
        const target = el.duration ? f * el.duration : 0;
        if (mode.value === 'hls') {
            el.currentTime = target;

            syncTimelineFromDom();

            return;
        }
        if (mode.value === 'mp3_ready' && Number.isFinite(el.duration) && el.duration > 0) {
            const seekableEnd = el.seekable.length ? el.seekable.end(el.seekable.length - 1) : el.duration;
            el.currentTime = Math.min(Math.max(target, 0), seekableEnd);
            syncTimelineFromDom();
        }
    }

    return {
        visible,
        errorMessage,
        trackCodeUpper,
        trackTitle,
        trackArtist,
        trackAlbum,
        coverArtUrl,
        trackEntityId,
        trackRecordId,
        trackArtistId,
        trackAlbumId,
        displayTitle,
        subtitle,
        mode,
        downloadFraction,
        activeDescriptorSource,
        currentTimeSeconds,
        durationSeconds,
        isPaused,
        // queue
        queueEntries,
        currentQueueIndex,
        currentEntry,
        hasQueue,
        canGoNext,
        canGoPrevious,
        currentEntrySupportsDeepCheck,
        repeatMode,
        shuffleEnabled,
        queueTotalCount,
        queueTruncated,
        queueLabel,
        prefetchEntryId,
        prefetchObjectUrl,
        replaceQueue,
        playListInContext,
        enqueue,
        jumpToEntry,
        removeEntry,
        reorderQueue,
        clearUpcoming,
        setShuffle,
        cycleRepeatMode,
        playNext,
        playPrevious,
        playEntryAtIndex,
        onTrackEnded,
        onAudioElementError,
        restoreFromPersistence,
        // playback
        attachAudio,
        syncTimelineFromDom,
        loadAndPlay,
        loadAndPlayEntry,
        loadAndPlayLocalFile,
        loadAndPlayWithDescriptor,
        play,
        pause,
        togglePlay,
        seekToFraction,
        close,
        suspend,
        teardownPlayback,
    };
});
